import { FirebaseAuthTypes } from '@react-native-firebase/auth';
import * as Sentry from '@sentry/react-native';
import { useLocalSearchParams } from 'expo-router';
import { ConfirmationResult } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { signInPhoneNumber } from '@/api/functions/authQuery';
import BrandedButton from '@/components/buttons/BrandedButton';
import DotsLoader from '@/components/DotsLoader';
import FormWrapper from '@/components/forms/FormWrapper';
import CodeInput from '@/components/inputs/CodeInput';
import Text from '@/components/Text';
import { SIGN_IN_BUTTON_BOTTOM } from '@/constants/layout';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useColor } from '@/hooks/useColor';
import useCountdown from '@/hooks/useCountdown';
import { i18n } from '@/utils/i18n';
import { handlePhoneAuthError } from '@/utils/loginErrorsHandler';

const VerifyCodeScreen = () => {
  const colors = useColor();
  const { phoneNumber, buyingShow } = useLocalSearchParams<{ phoneNumber: string; buyingShow: string }>();

  const { state, dispatch: dispatchAuth } = useAuth();

  const { showSuccess, showError } = useEndFlowModal();
  const [code, setCode] = useState<string>('');
  const [confirmation, setConfirmation] = useState<
    FirebaseAuthTypes.ConfirmationResult | ConfirmationResult | undefined
  >(state.signUpData?.confirmation);
  const [isChecking, setIsChecking] = useState(false);
  const [resolvingCaptcha, setResolvingCaptcha] = useState(false);

  const { time, reset } = useCountdown(30);

  const verifyCodeTitle = i18n.t('sign_in.individual.verify.title');
  const verifyCodeButton = i18n.t('sign_in.individual.verify.button');
  const verifyCodeVerificationMessage = `${i18n.t('sign_in.individual.verify.message')} ${phoneNumber}`;
  const verifyCodeSuccessTitle = i18n.t('sign_in.individual.verify.title_success');
  const verifyCodeTitleError = i18n.t('sign_in.individual.verify.title_error');
  const verifyCodeMessageError = i18n.t('sign_in.individual.verify.message_error');

  const checkCode = async (code: string) => {
    setIsChecking(true);
    if (buyingShow) {
      dispatchAuth({
        type: 'BUYING_SHOW',
        isBuyingShow: true
      });
    }
    try {
      await confirmation?.confirm(code);
    } catch (e) {
      showError({
        title: verifyCodeTitleError,
        message: verifyCodeMessageError
      });
      Sentry.captureException(e);
      setIsChecking(false);
      dispatchAuth({
        type: 'BUYING_SHOW',
        isBuyingShow: false
      });
    }
  };

  const onReSend = () => {
    setResolvingCaptcha(true);
    signInPhoneNumber({ phoneNumber: phoneNumber, captchaTag: 'recaptcha-verify-container' })
      .then((data) => {
        setConfirmation(data);
        showSuccess({
          title: verifyCodeSuccessTitle,
          message: verifyCodeVerificationMessage
        });
        setResolvingCaptcha(false);
        reset();
      })
      .catch((e) => {
        setResolvingCaptcha(false);
        const { title, message } = handlePhoneAuthError(e);
        showError({
          title: title,
          message: message
        });
        Sentry.captureException(e);
      });
    reset();
  };

  useEffect(() => {
    if (code && code.length === 6 && !resolvingCaptcha) checkCode(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <FormWrapper keyboardVerticalOffset={0} scrollEnable={false}>
      <SafeAreaView style={styles.flex}>
        <View style={styles.container}>
          <View style={styles.contentWrapper}>
            <Text style={[styles.titleText, { color: colors.titleText }]} fontWeight="700">
              {verifyCodeTitle}
            </Text>
            <View style={styles.codeInputWrapper}>
              <CodeInput value={code} setValue={setCode} />
            </View>
            <Text style={styles.terms}>{verifyCodeVerificationMessage}</Text>
            {isChecking && (
              <View style={styles.loadingWrapper}>
                <DotsLoader color={colors.primary} />
              </View>
            )}
          </View>
        </View>

        {Platform.OS === 'web' && (
          <div id="recaptcha-verify-container" style={{ display: resolvingCaptcha ? 'flex' : 'none' }} />
        )}
        {Platform.OS === 'web' && resolvingCaptcha && (
          <Text style={styles.captchaHelper}>{i18n.t('mix.recaptcha_helper')}</Text>
        )}
        <BrandedButton
          style={styles.btnStyle}
          loading={resolvingCaptcha}
          disabled={resolvingCaptcha || time > 0}
          onPress={onReSend}>
          {verifyCodeButton} {time > 0 ? ` (${time})` : undefined}
        </BrandedButton>
      </SafeAreaView>
    </FormWrapper>
  );
};

export default VerifyCodeScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    marginTop: 100
  },

  btnStyle: {
    width: '100%',
    marginBottom: SIGN_IN_BUTTON_BOTTOM
  },

  titleText: {
    fontSize: 22,
    marginBottom: 50
  },

  contentWrapper: {
    width: '90%',
    alignItems: 'center',
    flex: 1
  },

  terms: {
    fontSize: 14,
    marginTop: 45,
    paddingHorizontal: 10
  },

  flex: {
    flex: 1
  },

  codeInputWrapper: {
    width: '100%'
  },

  loadingWrapper: {
    flex: 1,
    alignItems: 'center',
    marginTop: 30,
    width: '100%'
  },

  captchaHelper: {
    paddingVertical: 10
  }
});
